import {
  portDataFormatted,
  modbusDataFormatted,
  modbusSlaveFormatted,
  clonePortsData,
  DIGITAL_OUTPUT,
  isModbusGateway,
  parseCSV,
} from '@/js/utils';
import {
  createDeviceSettingData,
  getDeviceValue,
  setDeviceValue,
} from '@/js/DeviceSetting';
import Vue from 'vue';
let timeoutFunc;

const SET_PORTS_DATA = (state, { data, uid }) => {
  if (!Array.isArray(data) || !data.length) return;
  const setFunc = (dev) => {
    const index = state.portsData.findIndex(
      (ports) => ports.mac_addr === dev.mac_addr
    );
    const newPortsData = isModbusGateway(dev.dev_model)
      ? modbusDataFormatted(dev)
      : portDataFormatted(dev);
    /*
    if (newPortsData.modbus_slaves) {
      const slaves = [...newPortsData.modbus_slaves, ...newPortsData.hmis].sort(
        (a, b) => a.port_id_dec - b.port_id_dec
      );
      newPortsData.slaves = slaves;
    }
    */

    if (index === -1) {
      state.portsData.push(newPortsData);
    } else {
      /*
      if (state.portsData[index].modbus_slaves) {
        newPortsData.modbus_slaves = state.portsData[index].modbus_slaves;
      }
      */
      Vue.set(state.portsData, index, newPortsData);
    }
  };
  if (!uid) {
    data.forEach(setFunc);
  } else {
    const target = data.find((device) => device.dev_id === uid);
    if (target) setFunc(target);
  }
};

const SET_PORTS_LOG_LINK = (state, { data, uid }) => {
  // Vue.set(state.portLogLink, uid, data);
};

const SET_SETTING_DATA = (state, { mac_addr, target }) => {
  if (timeoutFunc) clearTimeout(timeoutFunc);
  if (state.settingData) {
    state.settingTarget = target;
    return;
  }

  const portData = state.portsData.find((ports) => ports.mac_addr === mac_addr);
  if (portData) {
    state.settingData = clonePortsData(portData);
    state.settingOriginal = Object.freeze(clonePortsData(portData));
    state.configData = getDeviceValue(
      createDeviceSettingData(state.settingData.dev_model),
      state.settingData
    );
  }

  state.settingTarget = target;
  state.settingView = true;
};

const CLEAR_SETTING_DATA = (state) => {
  state.settingData = null;
  state.settingTarget = null;
  state.highlightList = [];
  state.settingPopup = null;
  state.settingOriginal = null;
  state.configData = null;
  if (timeoutFunc) clearTimeout(timeoutFunc);
  timeoutFunc = setTimeout(() => {
    state.settingView = false;
  }, 300);
};

const SET_OPENING_POPUP = (state, payload) => {
  state.settingPopup = payload;
};

const SET_DAILY_LOG_DATA = (state, payload) => {
  const dailyLogData = Object.keys(payload.data)[0];
  if (!state.historyData[payload.dev_id]) {
    state.historyData[payload.dev_id] = {};
  }
  if (payload.data[dailyLogData].trim()) {
    Vue.set(
      state.historyData[payload.dev_id],
      dailyLogData,
      parseCSV(payload.data[dailyLogData])
    );
  }
};

const SET_HOURLY_LOG_DATA = (state, payload) => {
  const hourlyLogData = Object.keys(payload.data)[0];
  if (!state.historyData[payload.dev_id]) {
    state.historyData[payload.dev_id] = {};
  }

  const raw = payload.data[hourlyLogData];
  if (!raw || !raw.trim()) return;

  const parts = hourlyLogData.split('_');
  const date = parts[0];
  const hourPart = parts[1];
  const hour = Number(hourPart);

  const newHourlyData = parseCSV(raw);

  // If hour is invalid, fall back to simple set (keep it simple)
  if (!Number.isFinite(hour) || hour < 0 || hour > 23) {
    Vue.set(state.historyData[payload.dev_id], hourlyLogData, newHourlyData);
    return;
  }

  const existingData = state.historyData[payload.dev_id][date] || [];

  const getTimestamp = (row) => {
    if (!row) return 0;
    // Common timestamp keys: Timestamp, timestamp, time, ts
    const ts = row.Timestamp ?? row.timestamp ?? row.time ?? row.ts ?? null;
    if (!ts) return 0;
    const t = new Date(String(ts));
    return isNaN(t.getTime()) ? 0 : t.getTime();
  };

  // Remove rows that belong to the same hour (keep rows without valid timestamp)
  const filtered = existingData.filter((item) => {
    const ms = getTimestamp(item);
    if (!ms) return true;
    return new Date(ms).getHours() !== hour;
  });

  const merged = [...filtered, ...newHourlyData].sort((a, b) => getTimestamp(a) - getTimestamp(b));

  Vue.set(state.historyData[payload.dev_id], date, merged);
};

const UPDATE_SETTING_DATA = (state, { parent, code, val }) => {
  const item = state.configData.find(
    (el) => el.parent === parent && el.code === code
  );

  if (!item) {
    if (code === 'events') {
      const modbusPort = state.configData.find((el) => el.code === 'ports');
      if (modbusPort && modbusPort.value.length) {
        const port = modbusPort.value.find((el) => el.port_id === parent);
        if (port) {
          Vue.set(port, code, val);
        }
      }
    }
  } else if (code === 'ports') {
    if (val.length) {
      val.forEach((valElement) => {
        const matchingItem = item.value.find(
          (itemElement) => itemElement.port_id === valElement.port_id
        );
        if (matchingItem) {
          Vue.set(valElement, 'events', matchingItem.events);
        }
      });
    }
  }

  if (item) {
    Vue.set(item, 'value', val);
  }
  setDeviceValue(state.configData, state.settingData);
};

const SET_PROMISE = (state, { uid, promise }) => {
  state.promise[uid] = promise;
};

const SET_SHARED_PROMISE = (state, { uid, promise }) => {
  state.sharedPromise[uid] = promise;
};

const SET_HISTORY_PROMISE = (state, { dev_id, date, hour, promise }) => {
  if (!state.historyPromise[dev_id]) {
    state.historyPromise[dev_id] = {};
  }

  if (hour === undefined) {
    state.historyPromise[dev_id][date] = promise;
    return;
  }

  if (!state.historyPromise[dev_id][date + '_' + hour]) {
    state.historyPromise[dev_id][date + '_' + hour] = {};
  }
  state.historyPromise[dev_id][date + '_' + hour] = promise;
};

const SET_SHARED_PORTS_DATA = (state, { data, uid, viewId }) => {
  if (!Array.isArray(data) || !data.length) return;
  const setFunc = (p) => {
    const index = state.sharedPortData.findIndex(
      (ports) => ports.port_id === p.port_id
    );
    if (index === -1) {
      const portData =
        p.port_type === 'MB' || p.port_type === 'HMI'
          ? modbusSlaveFormatted(p)
          : p;
      state.sharedPortData.push({ ...portData, viewIds: [viewId] });
    } else {
      const viewIds = [
        ...new Set([...state.sharedPortData[index].viewIds, viewId]),
      ];
      Vue.set(state.sharedPortData, index, { ...p, viewIds });
    }
  };
  if (!uid) {
    data.forEach(setFunc);
  } else {
    const target = data.find((p) => p.port_id === uid);
    if (target) setFunc(target);
  }
};

const SET_SHARED_SLAVES_DATA = (state, { data, uid, viewId }) => {
  if (!Array.isArray(data) || !data.length) return;
  const setFunc = (p) => {
    const index = state.sharedSlaveData.findIndex(
      (slave) => slave.port_id === p.port_id
    );
    if (index === -1) {
      state.sharedSlaveData.push({ ...p, viewIds: [viewId] });
    } else {
      const viewIds = [
        ...new Set([...state.sharedSlaveData[index].viewIds, viewId]),
      ];
      Vue.set(state.sharedSlaveData, index, { ...p, viewIds });
    }
  };
  if (!uid) {
    data.forEach(setFunc);
  } else {
    const target = data.find((p) => p.port_id === uid);
    if (target) setFunc(target);
  }
};

const REMOVE_PORTS_DATA = (state, uid) => {
  const index = state.portsData.findIndex(
    (ports) => ports.mac_addr === uid || ports.dev_id === uid
  );
  if (index !== -1) {
    // Remove actions containing removed ports
    if (Array.isArray(state.portsData[index].ports)) {
      const tobeRemovedPorts = state.portsData[index].ports
        .filter((p) => p.port_type === DIGITAL_OUTPUT)
        .map((port) => port.port_id);
      if (tobeRemovedPorts.length) {
        state.portsData.forEach((portData) => {
          if (portData.mac_addr === uid || !Array.isArray(portData.ports)) {
            return;
          }
          portData.ports.forEach((p) => {
            if (Array.isArray(p.events)) {
              p.events.forEach((evt) => {
                evt.ctrl_actions = evt.ctrl_actions.filter(
                  (action) => tobeRemovedPorts.indexOf(action.port_id) === -1
                );
              });
            }
          });
        });
      }
    }
    state.portsData.splice(index, 1);
    delete state.promise[uid];
  }
};

const SET_HIGHLIGHT = (state, { tab, code, detail }) => {
  if (!state.highlightList.find((el) => el.tab === tab && el.code === code)) {
    state.highlightList.push({ tab, code, detail });
  }
};

const CLEAR_HIGHLIGHT = (state, { tab, code }) => {
  const index = state.highlightList.findIndex(
    (el) => el.tab === tab && el.code === code
  );
  if (index !== -1) state.highlightList.splice(index, 1);
};

const SET_MODBUSSLAVE_DATA = (state, { dev_id, data }) => {
  const target = state.portsData.find((device) => device.dev_id === dev_id);
  if (!target || !data) {
    return;
  }

  if (!Array.isArray(target.ports)) {
    Vue.set(target, 'ports', []);
  }

  if (Array.isArray(data)) {
    Vue.set(target, 'ports', data);
    return;
  }

  const setFunc = (slv) => {
    const index = target.ports.findIndex(
      (slave) =>
        slave.port_id === slv.port_id && slave.req_func === slv.req_func
    );
    if (index === -1) {
      target.ports.push(slv);
    } else {
      Vue.set(target.ports, index, slv);
    }
  };
  setFunc(data);
};

const REMOVE_MODBUSSLAVE_DATA = (state, { port_id }) => {
  if (!port_id) return;
  let slaveIndex = -1;
  const myModbus = state.portsData.find((p) => {
    if (!Array.isArray(p.ports)) return;
    const slaveIdx = p.ports.findIndex((slave) => slave.port_id === port_id);
    if (slaveIdx !== -1) {
      slaveIndex = slaveIdx;
      return true;
    }
  });

  if (slaveIndex >= 0) {
    myModbus.ports.splice(slaveIndex, 1);
  }
};

const SET_PENDING_VALIDATION = (state, value) => {
  state.pendingValidation = value;
};

const CLEAR_PENDING_VALIDATION = (state) => {
  state.pendingValidation = false;
};

export default {
  SET_PORTS_DATA,
  SET_PORTS_LOG_LINK,
  SET_SETTING_DATA,
  CLEAR_SETTING_DATA,
  UPDATE_SETTING_DATA,
  SET_PROMISE,
  SET_HISTORY_PROMISE,
  REMOVE_PORTS_DATA,
  SET_HIGHLIGHT,
  CLEAR_HIGHLIGHT,
  SET_OPENING_POPUP,
  SET_SHARED_PROMISE,
  SET_SHARED_PORTS_DATA,
  SET_SHARED_SLAVES_DATA,
  SET_MODBUSSLAVE_DATA,
  REMOVE_MODBUSSLAVE_DATA,
  SET_DAILY_LOG_DATA,
  SET_HOURLY_LOG_DATA,
  SET_PENDING_VALIDATION,
  CLEAR_PENDING_VALIDATION,
};
